// Edit-and-resubmit command (EPIC #157, phase P5.9 — design §2.2).
//
// The preprocess-panel "edit the question, then resubmit" handler (`editResubmit`) moved
// VERBATIM out of AppInner's render into a `(deps, args)` command. It is a submit-sibling:
// cancel the in-flight run → drop the old pending run → show an edited placeholder →
// re-preprocess the edited text → reject past dates → refine the placeholder → hand off to
// `doForecast` (the already-extracted submit command).
//
// Despite its length this op has NO §7 booby-traps — it never reads the live store and
// never touches the exec table. The only migration seams are:
//   • `dispatchRun(…)` → `deps.store.dispatch(…)`
//   • React handles (setters, imperative refs, the `autoZoom`/`dismiss`/`doForecast`
//     callbacks) threaded via the deps bags; `pp` (the pendingPreprocess snapshot) is an arg.
// `doForecast` is injected as a callback (option B/A split): this command orchestrates the
// edit + re-preprocess, then chains into the submit command via `deps.callbacks.doForecast`
// rather than re-importing it and rebuilding SubmitDeps.
//
// The re-preprocess goes through the typed `deps.services.api.preprocess` (P5.11 drained the
// raw fetch); the submit-abort signal is threaded so CLEAR can still cancel it mid-flight.
import { recordContractEdit } from "../lib/contractTelemetry";
import { recordTextEvent } from "../lib/telemetry/groups.js";
import { newMessageId } from "../lib/chatTurn.js";
import { runRemoved, runCreated, activeSet, contractSet } from "../lib/forecast/reducer.js";
import { makeRun, STATUS } from "../lib/forecast/model.js";
import { effectiveTriggerModel } from "../lib/forecast/engines.js";
import { contractFromPreprocessed, deadlineOf } from "../lib/forecast/forecastClient.js";
import { resolutionOptionsOf } from "../lib/forecast/resolutionChoice.js";
import { editBufferOf, routePreprocess } from "../lib/forecast/multiway.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { intentEmitter } from "../lib/telemetry/intentEmitter.js";
import { beginIntent } from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";

// ── deps typing ───────────────────────────────────────────────────────────────
// Pragmatic types at the React boundary (decision C): the store carries real domain types;
// the UI setters / imperative refs / callbacks are React handles this command doesn't own.
type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

/** React state + setters editResubmit reads/writes (ephemeral UI state; moves down in P7). */
interface EditResubmitUi {
  setForecastingPhase: AnyFn;
  setPendingPreprocess: AnyFn;
  compareMode: boolean;
  triggerLlm: any;
}

/** Stable imperative handles (§7 #2 abort refs among them). */
interface EditResubmitRefs {
  forecastAbortRef: Ref;
  submitAbortRef: Ref;
  slideCache: Ref;
}

/** App-provided callbacks editResubmit orchestrates; `doForecast` is the submit binding. */
interface EditResubmitCallbacks {
  autoZoom: AnyFn;
  dismiss: AnyFn;
  doForecast: AnyFn;
}

/** Full deps for the edit-resubmit command: shared base + the React-bound bags. */
export interface EditResubmitDeps extends CommandDeps {
  ui: EditResubmitUi;
  refs: EditResubmitRefs;
  callbacks: EditResubmitCallbacks;
}

/** Args: the pendingPreprocess snapshot the panel edits (React state, not in the store). */
export interface EditResubmitArgs {
  pp: any;
}

// uid: random unique id with a graceful fallback (mirrors App.jsx's module-level helper
// verbatim — crypto.randomUUID() is unavailable in non-secure contexts).
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/**
 * Edit the preprocessed question and resubmit. Cancels the in-flight run, swaps in an edited
 * placeholder, re-preprocesses the edited text, and hands off to `doForecast`. See the file
 * header for the seams; the body is byte-preserved from the AppInner original.
 */
export async function editResubmit(
  rawDeps: EditResubmitDeps,
  { pp }: EditResubmitArgs,
): Promise<void> {
  // Routed like the original submit (bug e87437b1), on the EDITED text: an edit can add or
  // drop a `B:` / `MW:` prefix, and a prefix-only edit is refused here, before the run is
  // touched. A snapshot without `routing` (a branch trigger, an older reload handle) keeps
  // the binary default.
  const routed = routePreprocess(pp.editText, pp.routing);
  const eq = routed.question;
  if (!eq || pp.submitting) return;
  const routing = { ...pp.routing, forced: routed.forced ? routed.mode : null };
  // The user changed the preprocessed contract before submitting — a distinct
  // ui.contract.edit event (delineated from ui.forecast.submit by name). Marker
  // only: whether the question text changed + its length, never the text.
  recordContractEdit({
    field: "question_text",
    forecastId: pp.clientId ?? null,
    changed: eq !== (pp.question.question_text || pp.original),
    len: eq.length,
  });
  // The edited question runs on a fresh pending run; minted here so the text event
  // names it (its `active_id`), not the run about to be removed.
  const pendingId = "fc-pending-" + uid();
  // The user's edit (P2 design §4.6): a new run replaces the submit's, which therefore ends
  // `cancelled`; the edit follows its own run from here.
  intentEmitter().endRun({ runId: pp.clientId }, "cancelled");
  // The edited question is a message of its own: the old run may already have
  // persisted the original under `pp.msgId`.
  const msgId = newMessageId();
  const intent = beginIntent(
    "forecast.edit_resubmit",
    { forecast_id: pendingId, parent_id: pp.clientId ?? null, msg_id: msgId },
    { origin: "preprocess.edit-submit" },
  );
  intent.attachRun(pendingId);
  const deps = bindIntent(rawDeps, intent);
  recordTextEvent({
    event: "text.contract_question",
    value: eq,
    len: eq.length,
    forecast_id: deps.store.getState()?.runsById?.[pp.clientId]?.forecastId ?? null,
    active_id: pendingId,
  });
  // STEP 1 — cancel the in-flight forecast IMMEDIATELY (before we await
  // preprocess) so its late-arriving response can't clobber the
  // placeholder we're about to set. The doForecast() success path bails
  // out when forecastAbortRef.current !== its own ac.
  if (deps.refs.forecastAbortRef.current) {
    deps.refs.forecastAbortRef.current.abort();
    deps.refs.forecastAbortRef.current = null;
  }
  // Drop the old pending run so its empty conversation doesn't linger in the
  // store. pp.clientId is the submit()-minted pendingId that editResubmit replaces.
  // The reducer no-ops if the run was already removed (e.g. by resetForecast).
  deps.store.dispatch(runRemoved(pp.clientId));
  // STEP 2 — show the awaiting state with the edited text immediately
  // so the user sees feedback that their edit took effect, rather than
  // having to wait for the second preprocess to complete.
  const initResDate = pp.question.scheduled_resolution_time
    ? new Date(pp.question.scheduled_resolution_time).toISOString().slice(0, 10)
    : new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
  // dual-write editResubmit placeholder creation + active to store (mirrors submit flow)
  deps.store.dispatch(
    runCreated(
      makeRun({
        runId: pendingId,
        conversationId: pendingId,
        question: eq,
        status: STATUS.PREPROCESSING,
        triggerModel: deps.ui.compareMode ? effectiveTriggerModel(deps.ui.triggerLlm) : null,
      }),
    ),
  );
  deps.store.dispatch(activeSet(pendingId));
  deps.ui.setForecastingPhase("awaiting");
  deps.refs.slideCache.current = {};
  // STEP 3 — re-preprocess the edited text so scheduled_resolution_time
  // matches the new wording. Without this the timeline would render the
  // edited question at the original question's resolution date.
  //
  // Arm `submitAbortRef` so resetForecast() (CLEAR) can abort this
  // preprocess fetch + bail out before doForecast fires. Mirrors the
  // submit() pattern — without this, a CLEAR landing during preprocess
  // would let the fetch resolve, fall through to the refine + dismiss
  // path, and kick off doForecast for an already-discarded forecast.
  const submitAc = new AbortController();
  deps.refs.submitAbortRef.current = submitAc;
  // Re-point pendingPreprocess.clientId at the NEW pendingId. The
  // panel's render gate is `active?.id===pendingPreprocess.clientId`,
  // and we've just swapped active to a fresh placeholder with a new
  // pendingId — without re-pointing, the gate goes false and the
  // panel unmounts mid-flight, hiding the submitting / submitError
  // states from the user. setActive + setPendingPreprocess batch in
  // this synchronous handler (pre-await), so the panel stays mounted
  // across the transition.
  deps.ui.setPendingPreprocess((p: any) => ({
    ...p,
    clientId: pendingId,
    msgId,
    routing,
    submitting: true,
    submitError: null,
  }));
  let editedPreprocessed;
  try {
    // Preprocess through the typed api seam (P5.11), threading submitAbortRef's signal.
    // `preprocess` throws a descriptive error on a non-ok / null-body response; on abort it
    // throws too (apiClient → {ok:false,status:0}), and the catch's `submitAc.signal.aborted`
    // check distinguishes a CLEAR abort from a genuine failure — same control flow as before.
    const parsed = await deps.services.api.preprocess(pp.engine, eq, {
      signal: submitAc.signal,
      mode: routed.mode,
      variantId: routing.variantId || "",
    });
    editedPreprocessed = { ...parsed, question_text: parsed.question_text || eq };
    intent.mark("preprocess_ms");
  } catch (err) {
    // CLEAR fired during the fetch — bail out entirely; resetForecast
    // has already torn down active/forecasts/phase so any state work
    // here would resurrect a forecast the user just discarded.
    if (submitAc.signal.aborted) {
      deps.store.dispatch(runRemoved(pendingId));
      intent.end("cancelled");
      return;
    }
    // Fall back: keep the stale preprocess (matches old behavior) but warn the user.
    console.warn("[editResubmit] Edit preprocess failed, reusing original preprocess:", err);
    // Drop the old wording's resolution CHOICES: they were computed for the previous
    // question, so holding the edit behind them could run a deadline that no longer
    // matches it. The resolved moment itself stays, as before.
    const {
      resolution_options: _staleOptions,
      resolution_unconfirmed: _staleUnconfirmed,
      ...keptMeta
    } = pp.question.custom_metadata || {};
    // A `B:` edit can't keep the old outcomes: a contract without an outcome space is binary.
    const { outcome_space: _staleSpace, ...staleBinary } = pp.question;
    const stale = routed.forced && routed.mode === "binary" ? staleBinary : pp.question;
    editedPreprocessed = { ...stale, question_text: eq, custom_metadata: keptMeta };
  }
  // CLEAR could also land between the fetch resolve and here (rare,
  // but possible if abort fired just as the response was deserializing).
  if (submitAc.signal.aborted) {
    deps.store.dispatch(runRemoved(pendingId));
    intent.end("cancelled");
    return;
  }
  // Reject past resolution dates (mirror submit())
  //
  // Through `deadlineOf`, NOT `new Date(scheduled_resolution_time)`: that field
  // is naive-UTC on the wire, so parsing it directly shifts the deadline by the
  // viewer's offset — rejecting a genuinely-future edit in a positive zone, and
  // accepting an already-past one in a negative zone.
  {
    const rd = deadlineOf(editedPreprocessed);
    if (rd && rd <= new Date()) {
      deps.ui.setForecastingPhase(null);
      // dual-write editResubmit past-date rejection to store (mirrors submit flow)
      deps.store.dispatch(activeSet(null));
      deps.store.dispatch(runRemoved(pendingId));
      deps.ui.setPendingPreprocess((p: any) =>
        p
          ? {
              ...p,
              submitting: false,
              submitError: `Resolution date (${rd.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}) is in the past.`,
            }
          : null,
      );
      intent.end("error");
      return;
    }
  }
  // STEP 4 — refine the placeholder with real preprocess data and kick off doForecast.
  // Shared helper (same shape as submit/resume; invalid date → initResDate fallback).
  const refinedContract = contractFromPreprocessed(editedPreprocessed, eq, initResDate);
  const resDate = refinedContract.resolution_date;
  // as any (decision C): contractFromPreprocessed's inferred shape has `probability: null`,
  // slightly wider than the reducer's `Contract` — Lane B's generated wire types tighten this.
  deps.store.dispatch(contractSet(pendingId, refinedContract as any)); // dual-write editResubmit contract refine to store (mirrors submit flow)
  deps.callbacks.autoZoom(resDate);
  // The edited wording can itself leave the deadline ambiguous — hold for the user's
  // pick exactly as submit does (commands/applyResolutionMoment starts the forecast).
  if (resolutionOptionsOf(editedPreprocessed).length > 0) {
    deps.ui.setPendingPreprocess((p: any) =>
      p
        ? {
            ...p,
            question: editedPreprocessed,
            editText: editBufferOf(editedPreprocessed.question_text || eq, routing),
            editing: false,
            submitting: false,
            submitError: null,
            awaitingChoice: true,
            paused: true,
          }
        : null,
    );
    return;
  }
  // Stay IN the panel: show the refreshed question and resolution so the user can
  // review what their edit produced, with auto-continue paused. CONTINUE confirms
  // (and marks the submission confirmed); the forecast runs meanwhile, as on submit.
  deps.ui.setPendingPreprocess((p: any) =>
    p
      ? {
          ...p,
          question: editedPreprocessed,
          editText: editBufferOf(editedPreprocessed.question_text || eq, routing),
          editing: false,
          submitting: false,
          submitError: null,
          paused: true,
          // The refreshed question needs no choice (or its stale ones were dropped):
          // leave the choose mode, or a held panel would show no options and no
          // CONTINUE and could never proceed.
          awaitingChoice: false,
        }
      : null,
  );
  deps.callbacks.doForecast(
    editedPreprocessed.question_text || eq,
    pp.engine,
    editedPreprocessed,
    null,
    pendingId,
    msgId,
    routing,
  );
}
